/**
 * Module-level reactors that tie `pathModeState` to its Cesium-side effects.
 *
 * Splitting these out of `PathModeDetails` matters because the panel
 * unmounts whenever path-mode's right-side column changes (minimize/maximize,
 * sub-tool active, etc.). Putting the effects in a `$effect.root` here means
 * they run for the lifetime of the app — section colours and mast labels
 * persist on the map regardless of which UI happens to be on screen.
 *
 * The `$effect.root` is created on first import (eagerly). `pathMode.ts`
 * imports this file purely for that side-effect.
 */

import { get } from 'svelte/store';

import type { IMastSectionInfoRequest } from '$lib/models/IMastSelectionModel';
import api from '$lib/services/kyAuthenticatedInstance';
import { toast } from '$lib/components/organisms/toast/toast';
import {
	onContextualizedCzml,
	onSectionCalculationRecalculated
} from '$lib/stores/polygon-section-stale-store';
import {
	contextualizedCzmlOverlapsSection,
	handleContextualizedCzmlResponse
} from '../map-utils';
import { getPlanQueryParameter } from '../map-mode-store';
import type { ISectionInfo } from '../map-tools-store';
import { applyLoadCases } from './pathToolLoadCases.svelte';
import {
	applyLoadCaseLineColors,
	applyMastLabels,
	applySectionStyling,
	clearMastLabels,
	clearSectionStyling
} from './pathToolStyling';
import { enrichSections } from './pathToolUtils';
import { pathModeState } from './path-mode-state.svelte';

/**
 * Fetches `Section/calculate/info` for the masts selected with the path tool and primes
 * `pathModeState`. Called automatically by the reactor below when the masts are set but
 * section info isn't, and can be called manually (e.g. by `refreshPathModeContext`)
 * after a flag-flip.
 *
 * Bypasses svelte-query because this runs at module scope where component
 * context isn't available. Loading/error state is mirrored into
 * `pathModeState.sectionInfo{Loading,Error}` so the panel UI keeps working.
 */
export async function fetchAndApplySectionInfo(): Promise<void> {
	const mastIds = $state.snapshot(pathModeState.mastIds);
	if (mastIds.length === 0) return;

	pathModeState.sectionInfoLoading = true;
	pathModeState.sectionInfoError = null;

	try {
		const body: IMastSectionInfoRequest = { mastIds, distanceControlLineTerrain: true };
		const data = await api
			.post(`Section/calculate/info` + get(getPlanQueryParameter), { json: body })
			.json<ISectionInfo>();

		const loadCases = data.loadCases ?? [];
		// Server-flagged default; fall back to first when none flagged.
		const defaultName = loadCases.find((lc) => lc.isDefault)?.name ?? loadCases[0]?.name;

		pathModeState.sectionInfo = data;
		pathModeState.infoLoadCases = loadCases;
		pathModeState.sectionInfoLoading = false;
		// Stiff-calc source. Overwritten by `runSectionCalculation` when the
		// user runs a full DSS calc.
		pathModeState.loadCaseSource = 'info';

		// Build loadcase datasources before flipping `activeLoadCases` so the
		// section-styling effect finds the new line entities ready to colour.
		// Pass the final active set so terrain visibility is applied directly
		// here rather than waiting on the watcher to re-fire from the assignment.
		const activeNames = defaultName ? [defaultName] : [];
		if (loadCases.length > 0) {
			await applyLoadCases(loadCases, activeNames);
		}

		if (defaultName) {
			pathModeState.activeLoadCases = [defaultName];
		}
	} catch (error) {
		const message =
			error instanceof Error ? error.message : 'Kunne ikke hente seksjonsinformasjon';
		pathModeState.sectionInfoError = message;
		pathModeState.sectionInfoLoading = false;
		toast({
			type: 'error',
			title: 'Feil ved uthenting av seksjonsinformasjon',
			description: message
		});
	}
}

/**
 * Applies a freshly-calculated `info` payload to `pathModeState`. Used by both
 * the in-tab `runSectionCalculation` flow (via direct call) and the cross-tab
 * broadcast from the report tab's regenerate. Mirrors the post-mutation block
 * of `runSectionCalculation` in `PathModeDetails.svelte`:
 *
 *   - Replaces `sectionInfo.loadCases` with the calculated set (no merging
 *     with the prior /info load cases — the most recent fetch is the single
 *     source of truth).
 *   - Carries `sectionLoadCases` (the modal's pickable cases) over from the
 *     previous sectionInfo since /calculate doesn't return that field.
 *   - Flips `loadCaseSource` to 'calculate' so the chip label updates.
 *   - Builds the CZML datasources for the new load cases before flipping
 *     `activeLoadCases`, so the colouring effect finds the line entities
 *     ready to be styled.
 *   - Auto-activates every freshly calculated load case so the user
 *     immediately sees results without an extra toggle.
 */
export async function applyRecalculatedSectionInfo(info: ISectionInfo): Promise<void> {
	const previous = pathModeState.sectionInfo;
	const calculatedLoadCases = info.loadCases ?? [];

	pathModeState.sectionInfo = {
		...info,
		loadCases: calculatedLoadCases,
		sectionLoadCases: previous?.sectionLoadCases ?? info.sectionLoadCases ?? []
	};
	pathModeState.loadCaseSource = 'calculate';
	pathModeState.calculationStatus = 'fresh';

	// Auto-activate every freshly calculated load case. Pass the names into
	// applyLoadCases so terrain visibility is applied synchronously against the
	// new selection — the subsequent assignment is then just for the styling
	// effect and other reactive consumers.
	const calculatedNames = calculatedLoadCases.map((lc) => lc.name);
	await applyLoadCases(calculatedLoadCases, calculatedNames);

	pathModeState.activeLoadCases = calculatedNames;
}

/**
 * After a LineDesign (Linear) run: puts the `/info` curves back on the map if a LineDesign Pro
 * run had replaced them, so the map never shows Pro curves next to a LineDesign result.
 */
export async function restoreInfoLoadCases(): Promise<void> {
	if (pathModeState.loadCaseSource !== 'calculate' || !pathModeState.sectionInfo) return;
	const loadCases = $state.snapshot(pathModeState.infoLoadCases);
	pathModeState.sectionInfo = { ...pathModeState.sectionInfo, loadCases };
	pathModeState.loadCaseSource = 'info';

	const defaultName = loadCases.find((lc) => lc.isDefault)?.name ?? loadCases[0]?.name;
	const activeNames = defaultName ? [defaultName] : [];
	await applyLoadCases(loadCases, activeNames);
	pathModeState.activeLoadCases = activeNames;
}

// ── Reactors ────────────────────────────────────────────────────────────────

$effect.root(() => {
	// 1. Auto-fetch /info as soon as the path is finished (or after a
	// refresh clears sectionInfo). Runs independent of any component being
	// mounted — fixes the case where the panel is minimized and a sub-tool
	// edit refreshes state.
	$effect(() => {
		const hasMasts = pathModeState.mastIds.length > 0;
		const hasInfo = pathModeState.sectionInfo !== null;
		const isLoading = pathModeState.sectionInfoLoading;
		// `sectionInfoError` is part of the guard so a failed /info (e.g. 403)
		// doesn't loop forever: the catch block flips `sectionInfoLoading` back
		// to false, which would otherwise re-fire this effect indefinitely.
		// Every refresh path (mutation hook in `map-utils`,
		// `resumePathModeAfterSubtool`, `refreshPathModeContext`) clears
		// `sectionInfoError`, so genuine retries still trigger.
		const hasError = pathModeState.sectionInfoError !== null;
		if (!hasMasts || hasInfo || isLoading || hasError) return;
		void fetchAndApplySectionInfo();
	});

	// 2. Section line colouring (plus optional load-case overlay). Persists
	// across panel mount/unmount — previously this lived in
	// `PolygonSelectionDetails`, so colours dropped the moment the panel
	// minimized.
	$effect(() => {
		const sections = enrichSections(pathModeState.sectionInfo);
		// Re-run on active-loadcase changes too: new loadcase datasources need
		// their line entities coloured, and the styling helper bakes that into
		// `applySectionStyling`.
		void pathModeState.activeLoadCases;
		const colorMode = pathModeState.colorMode;
		if (sections.length === 0) {
			clearSectionStyling();
			return;
		}
		applySectionStyling(sections);
		if (colorMode === 'loadcase') {
			applyLoadCaseLineColors(pathModeState.sectionInfo?.loadCases ?? []);
		}
	});

	// 3. Mast labels. Same lifecycle concern as section colours.
	$effect(() => {
		const masts = pathModeState.sectionInfo?.masts;
		if (masts && masts.length > 0) applyMastLabels(masts);
		else clearMastLabels();
	});

	// 4. Cross-tab regenerate: when the report tab re-runs the calculation it
	// broadcasts the fresh `info` payload. Mirror the same post-mutation
	// updates that `runSectionCalculation` does in-tab, so the map's loadcase
	// datasources, chip label and active loadcases stay in sync.
	//
	// No-op if path-mode has been left in this tab — there's nothing on screen
	// to update, and applying stale info on top of a non-path-mode map would
	// only confuse things if the user returns.
	$effect(() => {
		const unsubscribe = onSectionCalculationRecalculated((info) => {
			if (!pathModeState.active) return;
			void applyRecalculatedSectionInfo(info);
		});
		return unsubscribe;
	});

	// 5. Cross-tab CZML broadcast: another tab mutated entities (e.g. the report
	// tab's JBD pole-diameter update) and broadcast the raw
	// ContextualizedCzmlResponse. Pipe it through the same handler the in-tab
	// mutation hooks use so the affected entities redraw and — when they
	// include one of this tab's selected masts — the section /info auto-refreshes.
	//
	// Several Cesium tabs may be open. When path-mode is active, only the tab
	// whose selected masts include a changed mast should react; the others
	// ignore the broadcast. When path-mode is inactive there are no selected
	// masts to gate on, so just redraw to reflect the new data.
	$effect(() => {
		const unsubscribe = onContextualizedCzml((response) => {
			if (pathModeState.active && !contextualizedCzmlOverlapsSection(response)) return;
			void handleContextualizedCzmlResponse(response);
		});
		return unsubscribe;
	});
});
